import { defineStore } from 'pinia'
import { client as api } from '@clevver/api/clevver_client'
import {
  File,
  ViewMode,
  Tag,
  Quota,
  Pagination
} from '@clevver/types'

import { GetFilesParams } from '@clevver/api/clevver_client'
import { clevverEmitter } from '@clevver/stores/event.store'

interface FileFilter {
  viewMode?: ViewMode,
  tags: Tag[],
  page?: number,
}

interface IFilesState {
  filter: FileFilter
  files: File[] | null
  tags: Tag[]
  tagCounts: Record<string, number>
  quota: Quota
  currentTag?: Tag
  currentFile?: File
  pagination?: Pagination
}

const initialState: IFilesState = {
  filter: { tags: [], viewMode: ViewMode.untagged },
  quota: { used: 0, total: 0, available: 0, renews_on: 0 },
  files: [],
  tags: [],
  tagCounts: {},
  currentTag: undefined,
  currentFile: undefined,
  pagination: undefined,
}

export const useFilesStore = defineStore('files.store', {
  state: (): IFilesState => ({ ...initialState }),

  getters: {
    queryFromFilter(state) {
      let query = {}

      if (state.filter.viewMode != ViewMode.untagged) {
        query['mode'] = state.filter.viewMode
      }

      if (state.filter.tags.length > 0) {
        query['tags'] = state.filter.tags.map(t => t).join(',')
      }

      if (state.filter.page && state.filter.page > 1) {
        query['page'] = state.filter.page
      }

      return query
    },

    quotaPercentage(state) {
      if (state.quota) {
        if (state.quota.total == 0) { return 100 }

        return state.quota.used / state.quota.total
      }

      return 100
    },

    selectedTags(state): Tag[] {
      return state.filter.tags || []
    }
  },

  actions: {
    async setViewMode(mode: ViewMode) {
      this.filter.viewMode = mode

      // If the view changed to untagged, we need to reset the selected tags
      // and fetch the files.
      if (mode == ViewMode.untagged) {
        this.resetTags(false)
      }

      // If the mode changes then we also need to reset the page to 1
      this.filter.page = 1

      this.refreshFiles()
    },

    async fetchQuota() {
      try {
        const response = await api.getQuota()
        this.quota = response.data as Quota
      } catch(error) {
        console.log("Couldn't load quota")
      }
    },

    async getFiles(params: GetFilesParams = {}) {
      try {
        let { page, tags, archived, viewMode } = params

        // If tags is selected, and filter is set to untagged, we need
        // to remove the untagged filter, as it doesn't make sense to
        // have both selected at the same time.
        if (tags && tags.length > 0 && viewMode === ViewMode.untagged) {
          viewMode = ViewMode.all
        }

        const response = await api.getFiles({ page, tags, archived, viewMode })
        this.files = response.data
        this.pagination = response.pagination
        this.tags = response.meta.tags
        this.tagCounts = response.meta.tag_counts
      } catch(error) {
        // TODO: FIXME: Come up with a way to display this error on the UI
        console.log("We couldn't load the files from the api... how do we handle this?")
      }
    },

    async setFilter(filter: FileFilter) {
      this.filter = filter
    },

    /*
     * This function is used to refresh the files in the store.
     *
     * The files are fetched based on the current view, and the selected tags,
     * and any other attributes or filters that may be set based on the
     * current application state.
     */
    async refreshFiles() {
      const params: GetFilesParams = {}

      if (this.filter.viewMode) {
        params.viewMode = this.filter.viewMode
      }

      if (this.filter.tags && this.filter.tags.length > 0) {
        params.tags = this.selectedTags
      }

      // params.page = this.pagination?.current_page || 1
      params.page = this.filter.page ?? 1

      console.log("gonna fetch from refresh files", params)
      console.log(this.pagination)

      this.getFiles(params)
    },


    async archiveFile(file: File): Promise<Boolean> {
      try {
        const response = await api.archiveFile(file.id)

        if (response.error) {
          clevverEmitter.emit('requestFailed', { message: response.error })

          return false
        }

        if (this.files) {
          this.files = this.files.filter(f => f.id !== file.id)
        }

        clevverEmitter.emit('requestSuccess', { message: "Your file has been archived." })

        return true
      } catch(error) {
        clevverEmitter.emit('requestFailed', { message: error.message })

        return false
      }
    },

    async getTags() {
      try {
        const response = await api.getTags()
        this.tags = response.data
      } catch(error) {
        console.log("Couldn't load tags")
      }
    },

    async selectTag(tag: Tag) {
      if (!this.filter.tags.includes(tag)) {
        this.filter.tags.push(tag)

        // If the tags changed, we need to reset the page to 1
        this.filter.page = 1
      }

      // If the view is set to untagged, we need to change it to
      // change it to 'all' so that we can display the selected tags.
      if (this.filter.viewMode == ViewMode.untagged) {
        this.filter.viewMode = ViewMode.all
      }
    },

    async deselectTag(tag: Tag) {
      this.filter.tags = this.filter.tags.filter(t => t !== tag)

      // If the tags changed, we need to reset the page to 1
      this.filter.page = 1
    },

    async resetTags(fetchFiles: boolean = true) {
      this.filter.tags = []

      if (fetchFiles) {
        this.getFiles()
      }
    },

    async setCurrentFile(file: File) {
      this.currentFile = file
    }
  }
})